// Ion Storm — creative timed event (Agent B), built on the space-storm
// timed-event mechanic. For the duration, all enemies and all projectiles
// (ally and enemy) move at 50% speed, and the player's shield regeneration
// is suppressed by ion interference.
//
// Determinism (GDD Appendix A §A.1): the effect is a pure function of world
// state and tick — no RNG at all, no Math.random, no wall-clock. All pool
// iteration is in pool_index order.
//
// Perf: one pass over the enemy pool (≤512) and one over the projectile
// pool (≤1536) per tick, plus a small id-set lookup per projectile. Well
// inside the 60 fps budget.
//
// Visuals (wired by the parent, not here): listen for —
//   'phase_change' { system: 'events', event_id: 'ion_storm', phase: 'start'|'end', duration }
// Suggested look: blue static overlay, electric arcs arcing between
// entities, desaturated/slowed particle motion while active.
//
// Integration note: this event's update() must run AFTER updateEnemies() and
// updateProjectiles() in the sim step (see game.ts) — it post-adjusts the
// motion those systems integrated this tick.

import type { WorldState } from '../state.js';
import { wrapArena, wrappedDelta } from '../collision.js';
import type { GameEventDef } from '../events-manager.js';

/** Event lifetime: 10 s at 60 Hz. */
const EVENT_DURATION_TICKS = 10 * 60;
/** Movement multiplier applied to enemies and projectiles. */
const SLOW_FACTOR = 0.5;

/**
 * Create the Ion Storm event def. All mutable state lives in this closure
 * and is fully reset in start()/end().
 *
 * Implementation notes:
 *
 * Enemies — enemy-ai.ts recomputes steering velocity and integrates position
 * in the same pass, so scaling `vel` cannot produce a clean slow (steering
 * re-accelerates from the scaled value, decaying toward a near-freeze).
 * Instead the event records each enemy's position at the end of every event
 * tick and, on the next tick, retracts half of the displacement that
 * enemy-ai just integrated. Net motion is exactly 50%, regardless of
 * steering, separation pushes, or telegraph freezes (frozen enemies have
 * zero displacement — a no-op). Side effect: `vel` still reads full speed,
 * so player aim prediction (predictAim) over-leads during the storm —
 * acceptable, and thematically "ion interference scrambles targeting".
 *
 * Projectiles — projectile velocity persists across ticks (set once at
 * spawn), so a one-time `vel *= 0.5` per projectile is an exact, sustained
 * slow. On end(), still-alive slowed projectiles get `vel *= 2.0`: 0.5 and
 * 2.0 are powers of two, so the pair is lossless in floating point. New
 * projectiles spawned during the storm are slowed the first time update()
 * sees them. Swept collision stays correct: travel per tick is halved, so
 * each segment is still fully swept, and `hit_ids` prevents repeat hits.
 *
 * Shield regen — updateShieldRegen() (called inside updateEnemies, outside
 * this event's file scope) is gated on `tick - last_damage_tick >= 180`.
 * Stamping `last_damage_tick` every event tick keeps that canonical gate
 * closed for the whole storm plus the standard 3 s recovery afterwards,
 * without touching combat.ts. Side effect: the base HP regen in game.ts
 * shares the same damage gate, so it pauses too — intended ("recovery
 * systems suppressed by ion interference"); green-galaxy healing is
 * unaffected (it is not damage-gated).
 */
export function createIonStormEvent(): GameEventDef {
  // Ids of projectiles already slowed this activation (entity ids are unique
  // per run). Membership-only; iteration never depends on Set order.
  const slowedProjectiles = new Set<number>();
  // Enemy id → position at the end of the previous event tick. Rebuilt fresh
  // each tick, so released ids never linger.
  let prevEnemyPos = new Map<number, { x: number; y: number }>();

  return {
    id: 'ion_storm',
    name: 'Ion Storm',
    duration: EVENT_DURATION_TICKS,

    start(world: WorldState, tick: number): void {
      slowedProjectiles.clear();
      prevEnemyPos.clear();
      world.events.emit(
        'phase_change',
        { system: 'events', event_id: 'ion_storm', phase: 'start', duration: EVENT_DURATION_TICKS },
        tick
      );
    },

    update(world: WorldState, tick: number, _progress: number): void {
      // Suppress shield regen via the canonical damage gate (see above).
      if (world.player.alive) {
        world.player.last_damage_tick = tick;
      }

      // Slow every projectile exactly once.
      for (const p of world.projectiles.alive()) {
        if (slowedProjectiles.has(p.id)) continue;
        p.vel.x *= SLOW_FACTOR;
        p.vel.y *= SLOW_FACTOR;
        slowedProjectiles.add(p.id);
      }

      // Retract half of each enemy's displacement since the previous tick.
      const nextPrev = new Map<number, { x: number; y: number }>();
      for (const e of world.enemies.alive()) {
        const prev = prevEnemyPos.get(e.id);
        if (prev) {
          const d = wrappedDelta(e.pos, prev);
          e.pos.x = prev.x + d.x * SLOW_FACTOR;
          e.pos.y = prev.y + d.y * SLOW_FACTOR;
          wrapArena(e.pos);
        }
        nextPrev.set(e.id, { x: e.pos.x, y: e.pos.y });
      }
      prevEnemyPos = nextPrev;
    },

    end(world: WorldState, tick: number): void {
      // Restore projectiles still in flight (lossless power-of-two pair).
      for (const p of world.projectiles.alive()) {
        if (!slowedProjectiles.has(p.id)) continue;
        p.vel.x *= 1 / SLOW_FACTOR;
        p.vel.y *= 1 / SLOW_FACTOR;
      }
      slowedProjectiles.clear();
      prevEnemyPos.clear();
      world.events.emit(
        'phase_change',
        { system: 'events', event_id: 'ion_storm', phase: 'end' },
        tick
      );
    }
  };
}
